import Styles from './TieredUsage.module.scss'

import { FetchSubscriptionResponse } from 'api/clients/WorkspaceClient'
import { formatPrice } from 'components/Settings/TieredBilling/utils'
import moment from 'moment'
import { UsageTable } from '../UsageTable/UsageTable'
import UserProfile1 from 'blaze-ui/icons/20/UserProfile1'
import { AI, Brand, Facebook, Instagram, LinkedIn, Twitter } from 'blaze-ui/icons'
import FileSearch1 from 'blaze-ui/icons/20/FileSearch1'
import { Knowledge } from 'blaze-ui/icons/20/Knowledge'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { PlanIcon } from '../PlanIcon/PlanIcon'
import { Button } from 'blaze-ui/Button'
import { PricePill } from 'blaze-ui/Pricing/PricePill/PricePill'
import { useModals } from 'almanac-ui/Modal'
import { ManageSubscriptionModal } from 'components/ManageSubscriptionModal/ManageSubscriptionModal'
import { useEffect, useMemo } from 'react'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { TierUpgradeModal } from 'components/TierUpgradeModal/TierUpgradeModal'
import { getNextTierKey } from 'hooks/useTierUsageLimits'
import { Text } from 'blaze-ui/Text/Text'

interface Props {
  subscription: FetchSubscriptionResponse
  paymentPlan: PaymentPlan
}

export const TieredUsage = observer(({ subscription, paymentPlan }: Props) => {
  const nextPayment = subscription?.next_payment
  const cancelAtDate = subscription?.cancel_at ? moment(subscription?.cancel_at).format('ll') : undefined
  const tierUsageStore = useRootStore().tierUsageStore
  const workspace = useCurrentWorkspace()
  const { tierInfo, tierLimits, loading, initialized, paymentPlans } = tierUsageStore
  const { openModal } = useModals()

  const nextTier = useMemo(() => {
    const nextTierKey = getNextTierKey(paymentPlan.key)
    return paymentPlans.find((plan) => plan.key === nextTierKey)
  }, [paymentPlan, paymentPlans])

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, tierUsageStore, workspace])

  if (!tierInfo || !tierLimits || loading) return null

  const { brand_kits, source_materials, seats, ai_words, plagiarism_checks, integrations, published_posts } = tierInfo

  const {
    source_materials_limit,
    brand_kits_limit,
    seats_limit,
    ai_words_limit,
    plagiarism_checks_limit,
    integrations_limit,
    published_posts_limit,
  } = tierLimits

  if (workspace?.subscription_provider === 'apple') {
    return (
      <div className={Styles.container}>
        <div className={Styles.planContainer}>
          <div className={Styles.headerContainer}>
            <div>
              <div className={Styles.headerLeftContainer}>
                {paymentPlan.key !== PaymentPlanKey.EarlyAdopter && (
                  <div className={Styles.icon}>
                    <PlanIcon plan={paymentPlan.key} size={25} />
                  </div>
                )}
                <h2>{paymentPlan?.name} Plan</h2>
              </div>
            </div>
          </div>
          <div className={Styles.appStoreMessage}>
            <Text>
              Your subscription is currently managed through the Blaze app on your iPhone or iPad. To manage your plan,
              please open the Blaze app on your mobile device.
            </Text>
            <p></p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={Styles.container}>
      <div className={Styles.planContainer}>
        <div className={Styles.headerContainer}>
          <div>
            <div className={Styles.headerLeftContainer}>
              {paymentPlan.key !== PaymentPlanKey.EarlyAdopter && (
                <div className={Styles.icon}>
                  <PlanIcon plan={paymentPlan.key} size={25} />
                </div>
              )}
              <h2>{paymentPlan?.name} Plan</h2>
            </div>
            <div>
              {nextPayment && (
                <div className={Styles.paymentInfo}>
                  {nextPayment.amount < nextPayment.subtotal && (
                    <span className={Styles.originalPrice}>${formatPrice(nextPayment.subtotal)} USD</span>
                  )}
                  <p>
                    <span className={Styles.amount}>${nextPayment.amount}</span> USD due on{' '}
                    {moment(nextPayment.due_date).format('MMM Do, YYYY')}
                    {!!subscription?.trial_days_left && (
                      <>
                        <span className={Styles.trialConnector}>•</span>
                        {subscription?.trial_days_left} days left on trial
                      </>
                    )}
                  </p>
                </div>
              )}
              {cancelAtDate && (
                <div className={Styles.paymentInfo}>
                  <div>Cancels on {cancelAtDate}</div>
                  <div>You will not receive any new charges</div>
                </div>
              )}
            </div>
          </div>
          <div className={Styles.headerRightContainer}>
            <div className={Styles.upgradeButtonContainer}>
              <div className={Styles.pricePillContainer}>
                <PricePill plan={paymentPlan.key} />
              </div>
              <Button
                variant="tertiary"
                onPress={
                  nextTier
                    ? () =>
                        openModal(TierUpgradeModal, {
                          currentTier: paymentPlan,
                          reason: 'upgrade_plan',
                          upgradeTier: nextTier,
                          feature: 'Upgrade',
                        })
                    : undefined
                }
              >
                Upgrade
              </Button>
            </div>
            <Button variant="primary" onPress={() => openModal(ManageSubscriptionModal)}>
              Manage Subscription
            </Button>
          </div>
        </div>
      </div>
      <div className={Styles.tablesContainer}>
        <UsageTable
          title="Workspace"
          data={[
            { icon: <UserProfile1 />, label: 'Seats', value: seats, limit: seats_limit },
            { icon: <Brand size={20} />, label: 'Brand Kits', value: brand_kits, limit: brand_kits_limit },
            { icon: <AI size={20} />, label: 'AI Words', value: ai_words, limit: ai_words_limit },
            // { icon: <Cloud />, label: 'Cloud Storage', value: , limit: '1GB' },
            {
              icon: <FileSearch1 />,
              label: 'Plagiarism Checks',
              value: plagiarism_checks,
              limit: plagiarism_checks_limit,
            },
            {
              icon: <Knowledge size={20} />,
              label: 'Source Materials',
              value: source_materials,
              limit: source_materials_limit,
            },
          ]}
        />
        <div className={Styles.divider} />
        <UsageTable
          title="Accounts"
          data={[
            {
              icon: <Instagram size={20} />,
              label: 'Instagram',
              value: integrations.instagram,
              limit: integrations_limit,
            },
            {
              icon: <LinkedIn size={20} />,
              label: 'LinkedIn',
              value: integrations.linkedin,
              limit: integrations_limit,
            },
            {
              icon: <Twitter size={20} />,
              label: 'X/Twitter',
              value: integrations.x,
              limit: integrations_limit,
            },
            {
              icon: <Facebook size={20} />,
              label: 'Facebook',
              value: integrations.facebook,
              limit: integrations_limit,
            },
            {
              icon: <WordPressLogo size={20} color={'black'} />,
              label: 'WordPress',
              value: integrations.word_press,
              limit: integrations_limit,
            },
          ]}
        />
        <div className={Styles.divider} />
        <UsageTable
          title="Published Posts"
          data={[
            {
              icon: <Instagram size={20} />,
              label: 'Instagram',
              value: published_posts.instagram,
              limit: published_posts_limit,
            },
            {
              icon: <LinkedIn size={20} />,
              label: 'LinkedIn',
              value: published_posts.linkedin,
              limit: published_posts_limit,
            },
            {
              icon: <Twitter size={20} />,
              label: 'X/Twitter',
              value: published_posts.x,
              limit: published_posts_limit,
            },
            {
              icon: <Facebook size={20} />,
              label: 'Facebook',
              value: published_posts.facebook,
              limit: published_posts_limit,
            },
            {
              icon: <WordPressLogo size={20} color={'black'} />,
              label: 'WordPress',
              value: published_posts.word_press,
              limit: published_posts_limit,
            },
          ]}
        />
      </div>
    </div>
  )
})
